import { Column } from "primereact/column";
import {
  DataTable,
  DataTableExpandedRows,
  DataTableValueArray,
} from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import React, { useEffect, useState } from "react";
import { fetchEqbdRaterResults } from "../../../api/routes";
import {
  EqbdAccountResult,
  EqbdLocationResult,
} from "../../../client/types.gen";

type Props = {
  reportId: string;
  batchId: string | null;
  visible: boolean;
  onHide: () => void;
};

const usd = (value: number): string =>
  value.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

const fct = (value: number): string => value.toFixed(3);
const rate = (value: number): string => value.toFixed(6);

const moneyCol =
  (field: keyof EqbdLocationResult) => (row: EqbdLocationResult) =>
    usd(row[field] as number);

// --- Account pricing summary ---

const AccountSummary: React.FC<{
  account: EqbdAccountResult;
  locationCount: number;
}> = ({ account, locationCount }) => {
  const rows: [string, number][] = [
    ["Property Damage", account.pd_total],
    ["Business Income", account.bi_total],
    ["PD Options", account.pd_options_total],
    ["BI Options", account.bi_options_total],
    ["Utility Interruption", account.utility_total],
    ["Other Options", account.other_options_total],
    ["Structural", account.structural_total],
    ["Non-Owned Locations", account.non_owned_premium],
  ];
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 24,
        padding: "4px 0 16px",
      }}
    >
      <div>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>
          Account Pricing — {locationCount} location(s)
        </div>
        {rows.map(([label, value]) => (
          <div
            key={label}
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 24,
            }}
          >
            <span style={{ color: "var(--text-color-secondary)" }}>
              {label}
            </span>
            <span style={{ fontVariantNumeric: "tabular-nums" }}>
              {usd(value)}
            </span>
          </div>
        ))}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 24,
            fontWeight: 600,
            borderTop: "1px solid var(--surface-border)",
            marginTop: 4,
            paddingTop: 4,
          }}
        >
          <span>Account Total</span>
          <span style={{ fontVariantNumeric: "tabular-nums" }}>
            {usd(account.grand_total)}
          </span>
        </div>
        {account.min_premium_applied && (
          <div
            style={{ color: "var(--text-color-secondary)", fontSize: "0.85em" }}
          >
            Policy minimum premium applied (Rule 8)
          </div>
        )}
      </div>
      {account.refer_count > 0 && (
        <Tag
          severity="warning"
          value={`${account.refer_count} location(s) refer to company`}
        />
      )}
    </div>
  );
};

// --- Per-location audit detail (row expansion) ---

const FactorList: React.FC<{
  title: string;
  rows: [string, string][];
  totalLabel: string;
  total: string;
}> = ({ title, rows, totalLabel, total }) => (
  <div style={{ minWidth: 230 }}>
    <div style={{ fontWeight: 600, marginBottom: 6 }}>{title}</div>
    <table
      style={{ borderCollapse: "collapse", fontSize: "0.85em", width: "100%" }}
    >
      <tbody>
        {rows.map(([label, value]) => (
          <tr key={label}>
            <td
              style={{
                color: "var(--text-color-secondary)",
                padding: "2px 16px 2px 0",
              }}
            >
              {label}
            </td>
            <td
              style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}
            >
              {value}
            </td>
          </tr>
        ))}
        <tr>
          <td
            style={{
              fontWeight: 600,
              borderTop: "1px solid var(--surface-border)",
              paddingTop: 4,
            }}
          >
            {totalLabel}
          </td>
          <td
            style={{
              textAlign: "right",
              fontWeight: 600,
              fontVariantNumeric: "tabular-nums",
              borderTop: "1px solid var(--surface-border)",
              paddingTop: 4,
            }}
          >
            {total}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
);

const locationDetail = (row: EqbdLocationResult) => {
  const pd = row.pd_components;
  const bi = row.bi_components;
  if (!pd && !bi) {
    return (
      <div style={{ padding: "8px 48px" }}>
        No rating detail — SIC {row.sic_code || "(missing)"} did not map to an
        occupancy.
      </div>
    );
  }
  return (
    <div
      style={{
        display: "flex",
        gap: 40,
        flexWrap: "wrap",
        padding: "8px 16px 16px 48px",
      }}
    >
      {pd && (
        <FactorList
          title="Property Damage"
          rows={[
            ["Base rate (per $100)", rate(pd.base_rate)],
            ["Coverage mod", fct(pd.coverage_mod)],
            ["Limit factor", fct(pd.limit_factor)],
            ["Deductible factor", fct(pd.deductible_factor)],
            ["Risk factor", fct(pd.risk_factor)],
            ["Location discount", fct(pd.location_discount)],
            ["ACV adjustment", fct(pd.acv_adjustment)],
            ["Final rate (per $100)", rate(pd.final_rate)],
            ["Exposure (bldg + contents)", usd(pd.exposure)],
            ["State multiplier", fct(pd.state_multiplier)],
            ["Credit factor", fct(pd.credit_factor)],
            ["UW modifier", fct(pd.uw_modifier)],
          ]}
          totalLabel="PD premium"
          total={usd(row.pd_premium)}
        />
      )}
      {bi && (
        <FactorList
          title="Business Income"
          rows={[
            ["Base rate (per $100)", rate(bi.base_rate)],
            ["Coverage mod", fct(bi.coverage_mod)],
            ["Limit factor", fct(bi.limit_factor)],
            ["Deductible factor", fct(bi.deductible_factor)],
            ["Exposure factor", fct(bi.exposure_factor)],
            ["Risk factor", fct(bi.risk_factor)],
            ["Coinsurance factor", fct(bi.coinsurance_factor)],
            ["Location discount", fct(bi.location_discount)],
            ["Final rate (per $100)", rate(bi.final_rate)],
            ["Annual value", usd(bi.annual_value)],
            ["State multiplier", fct(bi.state_multiplier)],
            ["Credit factor", fct(bi.credit_factor)],
            ["UW modifier", fct(bi.uw_modifier)],
          ]}
          totalLabel="BI premium"
          total={usd(row.bi_premium)}
        />
      )}
      <FactorList
        title="Optional coverages"
        rows={[
          ["PD options", usd(row.pd_options_premium)],
          ["BI options", usd(row.bi_options_premium)],
          ["Utility interruption", usd(row.utility_premium)],
          ["Other options", usd(row.other_options_premium)],
          ["Structural", usd(row.structural_premium)],
        ]}
        totalLabel="Location total"
        total={usd(row.total_premium)}
      />
    </div>
  );
};

export const EqbdRateResultsDialog: React.FC<Props> = ({
  reportId,
  batchId,
  visible,
  onHide,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [locations, setLocations] = useState<EqbdLocationResult[]>([]);
  const [account, setAccount] = useState<EqbdAccountResult | null>(null);
  const [expandedRows, setExpandedRows] = useState<
    DataTableExpandedRows | DataTableValueArray | undefined
  >(undefined);

  useEffect(() => {
    if (!visible || !batchId) return;
    setLoading(true);
    setError(null);
    setLocations([]);
    setAccount(null);
    setExpandedRows(undefined);
    fetchEqbdRaterResults(reportId, batchId)
      .then((resp) => {
        setLocations(resp.locations);
        setAccount(resp.account);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : "Failed to load EQBD rates");
      })
      .finally(() => setLoading(false));
  }, [visible, reportId, batchId]);

  return (
    <Dialog
      header="Equipment Breakdown Premium"
      visible={visible}
      onHide={onHide}
      draggable={false}
      style={{ width: "95vw", maxWidth: "1600px" }}
    >
      {loading && (
        <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
          <ProgressSpinner />
        </div>
      )}
      {error && (
        <div style={{ color: "var(--red-500)", padding: 12 }}>{error}</div>
      )}
      {!loading && !error && locations.length === 0 && (
        <div style={{ padding: 12 }}>
          No EQBD rate data available for this run.
        </div>
      )}
      {!loading && !error && locations.length > 0 && (
        <>
          {account && (
            <AccountSummary
              account={account}
              locationCount={locations.length}
            />
          )}
          <div
            style={{
              color: "var(--text-color-secondary)",
              fontSize: "0.85em",
              paddingBottom: 6,
            }}
          >
            Expand a location to see how it rated (SOV inputs → factors →
            premium).
          </div>
          <DataTable
            value={locations}
            size="small"
            scrollable
            dataKey="address_id"
            expandedRows={expandedRows}
            onRowToggle={(e) => setExpandedRows(e.data)}
            rowExpansionTemplate={locationDetail}
          >
            <Column expander style={{ width: "3rem" }} />
            <Column field="premises_number" header="#" />
            <Column field="building_name" header="Location" />
            <Column field="state" header="State" />
            <Column field="sic_code" header="SIC" />
            <Column field="occupancy_code" header="Occ" />
            <Column header="PD" body={moneyCol("pd_premium")} />
            <Column header="BI" body={moneyCol("bi_premium")} />
            <Column header="PD Opt" body={moneyCol("pd_options_premium")} />
            <Column header="BI Opt" body={moneyCol("bi_options_premium")} />
            <Column header="Utility" body={moneyCol("utility_premium")} />
            <Column header="Other" body={moneyCol("other_options_premium")} />
            <Column header="Structural" body={moneyCol("structural_premium")} />
            <Column header="Total" body={moneyCol("total_premium")} />
            <Column
              header=""
              body={(row: EqbdLocationResult) =>
                row.refer ? <Tag severity="warning" value="Refer" /> : null
              }
            />
          </DataTable>
        </>
      )}
    </Dialog>
  );
};
